import * as THREE from 'three';
import { PieceType, PieceColor, Square } from '../core/types.ts';
import { PieceGeometryBuilder } from './pieceGeometries.ts';

export const TILE_SIZE = 2.0;

export function squareToCoords(square: Square): { x: number; y: number; z: number } {
  const file = square.charCodeAt(0) - 97; // 0..7 for a..h
  const rank = parseInt(square[1], 10) - 1; // 0..7 for 1..8

  const x = (file - 3.5) * TILE_SIZE;
  const z = (3.5 - rank) * TILE_SIZE;
  const y = 0.35; // Sitting flush on top of stone tile

  return { x, y, z };
}

export function coordsToSquare(x: number, z: number): Square | null {
  const file = Math.round(x / TILE_SIZE + 3.5);
  const rank = Math.round(3.5 - z / TILE_SIZE);

  if (file < 0 || file > 7 || rank < 0 || rank > 7) {
    return null;
  }

  const fileChar = String.fromCharCode(97 + file);
  const rankChar = (rank + 1).toString();
  return `${fileChar}${rankChar}` as Square;
}

export interface PieceObject {
  id: string;
  type: PieceType;
  color: PieceColor;
  square: Square;
  mesh: THREE.Mesh;
  originalY: number;
}

export class PieceViewManager {
  private group: THREE.Group;
  private pieces: Map<string, PieceObject> = new Map();
  private squarePieceMap: Map<Square, PieceObject> = new Map();

  // Materials
  private whiteMaterial: THREE.MeshStandardMaterial;
  private blackMaterial: THREE.MeshStandardMaterial;
  private whiteHighlightMaterial: THREE.MeshStandardMaterial;
  private blackHighlightMaterial: THREE.MeshStandardMaterial;

  constructor() {
    this.group = new THREE.Group();
    this.group.name = 'PiecesGroup';

    // White Army: Ethereal Ivory/Alabaster Stone with subtle gold sheen and cyan specular
    this.whiteMaterial = new THREE.MeshStandardMaterial({
      color: 0xefede6,
      roughness: 0.28,
      metalness: 0.18,
      emissive: 0x112233,
      emissiveIntensity: 0.2
    });

    // Black Army: Dark Volcanic Basalt/Obsidian with bronze edge reflection
    this.blackMaterial = new THREE.MeshStandardMaterial({
      color: 0x22242b,
      roughness: 0.32,
      metalness: 0.42,
      emissive: 0x180922,
      emissiveIntensity: 0.35
    });

    this.whiteHighlightMaterial = this.whiteMaterial.clone();
    this.whiteHighlightMaterial.emissive = new THREE.Color(0x00ccff);
    this.whiteHighlightMaterial.emissiveIntensity = 0.6;

    this.blackHighlightMaterial = this.blackMaterial.clone();
    this.blackHighlightMaterial.emissive = new THREE.Color(0xff0055);
    this.blackHighlightMaterial.emissiveIntensity = 0.6;
  }

  public getGroup(): THREE.Group {
    return this.group;
  }

  public getPieceAt(square: Square): PieceObject | null {
    return this.squarePieceMap.get(square) || null;
  }

  public getPieceById(id: string): PieceObject | null {
    return this.pieces.get(id) || null;
  }

  public getAllPieces(): PieceObject[] {
    return Array.from(this.pieces.values());
  }

  public createPiece(type: PieceType, color: PieceColor, square: Square, customId?: string): PieceObject {
    const id = customId || `${color}_${type}_${square}_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
    const geom = PieceGeometryBuilder.getGeometry(type);
    const mat = color === 'w' ? this.whiteMaterial.clone() : this.blackMaterial.clone();

    const mesh = new THREE.Mesh(geom, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.userData = { pieceId: id, type, color, square };

    const coords = squareToCoords(square);
    mesh.position.set(coords.x, coords.y, coords.z);

    // Orientation: White faces -Z (0 rad), Black faces +Z (Math.PI rad)
    mesh.rotation.y = color === 'w' ? 0 : Math.PI;

    // Give Knights an imposing dynamic 3/4 turn
    if (type === 'n') {
      mesh.rotation.y += color === 'w' ? 0.3 : -0.3;
    }

    const pieceObj: PieceObject = {
      id,
      type,
      color,
      square,
      mesh,
      originalY: coords.y
    };

    this.pieces.set(id, pieceObj);
    this.squarePieceMap.set(square, pieceObj);
    this.group.add(mesh);

    return pieceObj;
  }

  public updateSquare(piece: PieceObject, newSquare: Square): void {
    this.squarePieceMap.delete(piece.square);
    piece.square = newSquare;
    piece.mesh.userData.square = newSquare;
    this.squarePieceMap.set(newSquare, piece);
  }

  public removePiece(piece: PieceObject): void {
    this.squarePieceMap.delete(piece.square);
    this.pieces.delete(piece.id);
    this.group.remove(piece.mesh);
    piece.mesh.geometry.dispose();
    if (Array.isArray(piece.mesh.material)) {
      piece.mesh.material.forEach((m) => m.dispose());
    } else {
      piece.mesh.material.dispose();
    }
  }

  public clearAll(): void {
    const list = Array.from(this.pieces.values());
    for (const p of list) {
      this.removePiece(p);
    }
    this.pieces.clear();
    this.squarePieceMap.clear();
  }

  public highlightPiece(piece: PieceObject, highlight: boolean, isKingInCheck = false): void {
    const mat = piece.mesh.material as THREE.MeshStandardMaterial;
    if (highlight) {
      if (isKingInCheck) {
        mat.emissive.setHex(0xff1122);
        mat.emissiveIntensity = 0.9;
      } else {
        mat.emissive.setHex(piece.color === 'w' ? 0x00d2ff : 0xaa22ff);
        mat.emissiveIntensity = 0.75;
      }
    } else {
      // Revert to original
      mat.emissive.setHex(piece.color === 'w' ? 0x112233 : 0x180922);
      mat.emissiveIntensity = piece.color === 'w' ? 0.2 : 0.35;
    }
  }

  public setQuality(quality: 'high' | 'medium' | 'low'): void {
    const cast = quality !== 'low';
    this.pieces.forEach((p) => {
      p.mesh.castShadow = cast;
    });
  }
}
